/**
 * Paramate input registry
 *
 * Which live input elements are bound to which Paramate param, and which of
 * them are showing a value the server hasn't confirmed yet. Not a store — it
 * holds DOM references, not state, and nothing subscribes to it. It lived in
 * store.ts for a long time and made that file hard to describe.
 *
 * Entries hold a WeakRef to the element, so an input that is removed from the
 * page without unregistering doesn't keep it alive.
 */

import { devlog } from "../util/utils.ts";
import { pendingParamChanges } from "../components/paramate/lib/paramatePending.ts";
import { sendEv } from '../data/events.ts';
import { addAsGlobals } from '../asGlobals.ts';

/**
 * Registry entry for a paramate-linked input
 */
export interface ParamateLinkedInput {
  element: WeakRef<HTMLElement>;
  paramName: string;
  pendingValue: any | null;
  isPending: boolean;
}

/**
 * Registry for paramate-linked inputs
 * Maps param names (without 'main:' prefix) to their linked inputs
 */
export const paramateInputRegistry = new Map<string, Set<ParamateLinkedInput>>();

/**
 * Register an input element as linked to a paramate param
 */
export function registerParamateInput(paramName: string, element: HTMLElement): ParamateLinkedInput {
  if (!paramateInputRegistry.has(paramName)) {
    paramateInputRegistry.set(paramName, new Set());
  }

  const entry: ParamateLinkedInput = {
    element: new WeakRef(element),
    paramName,
    pendingValue: null,
    isPending: false
  };

  paramateInputRegistry.get(paramName)!.add(entry);
  return entry;
}

/**
 * Unregister an input element from the paramate registry
 */
export function unregisterParamateInput(paramName: string, element: HTMLElement): void {
  const entries = paramateInputRegistry.get(paramName);
  if (!entries) return;

  for (const entry of entries) {
    if (entry.element.deref() === element) {
      entries.delete(entry);
      break;
    }
  }

  if (entries.size === 0) {
    paramateInputRegistry.delete(paramName);
  }
}

/**
 * Mark all inputs for a param as pending
 */
export function markParamPending(paramName: string, pendingValue: any): void {
  const entries = paramateInputRegistry.get(paramName);
  if (!entries) return;

  for (const entry of entries) {
    const el = entry.element.deref();
    if (el) {
      entry.isPending = true;
      entry.pendingValue = pendingValue;
      el.classList.add('as-pending');
    }
  }
}

/**
 * Clear pending state for all inputs of a param without updating value
 */
export function clearParamPending(paramName: string): void {
  const entries = paramateInputRegistry.get(paramName);
  if (!entries) return;

  for (const entry of entries) {
    const el = entry.element.deref();
    if (el) {
      entry.isPending = false;
      entry.pendingValue = null;
      el.classList.remove('as-pending');
    }
  }
}

/**
 * Clear pending state for all inputs of a param and update with ground truth
 */
export function resolveParamPending(paramName: string, groundTruthValue: any): void {
  const entries = paramateInputRegistry.get(paramName);
  if (!entries) return;

  for (const entry of entries) {
    const el = entry.element.deref();
    if (el) {
      entry.isPending = false;
      entry.pendingValue = null;
      el.classList.remove('as-pending');

      // Dispatch update event to the element
      sendEv(el, 'as-paramate-value-resolved', {
        bubbles: false,
        detail: {
          paramName,
          value: groundTruthValue
        }
      });
    }
  }
}

/**
 * Check if any registered input has a pending configure value
 */
export function hasPendingConfigureValues(): boolean {
  for (const entries of paramateInputRegistry.values()) {
    for (const entry of entries) {
      if (entry.isPending) return true;
    }
  }
  return false;
}

/**
 * Trigger configure() on the configurator if there are pending values.
 * Values are already staged via setParamValue(..., false); this just
 * kicks off the configure cycle. Pending state is cleared when
 * paramate-configurator-after-configure resolves ground truth.
 * Returns the configure promise (undefined if there was nothing pending) so
 * callers can chain a callback onto completion.
 */
export function configurePending(configurator: any): Promise<any> | undefined {
  if (!hasPendingConfigureValues()) return undefined;
  // The staged values have to be named here. The SDK's configure() sends the
  // map it is given and nothing more — it never gathers the dirty params on
  // its own — so a bare configure() would be an empty PUT.
  const changes = pendingParamChanges(configurator);
  // configure() rejects on any doRequest failure (e.g. a 404 against a dead/expired
  // session) in addition to dispatching 'paramate-configurator-error', which
  // AsConfigurator.handleParamateError already classifies and surfaces. Left
  // unhandled, that same rejection also escapes as a raw "unhandled-rejection",
  // popping the generic error modal on top of the properly classified one.
  return Promise.resolve(configurator.configure(changes)).catch((err: any) =>
    devlog('[configurePending] configure() failed (handled via paramate-configurator-error):', err));
}

/**
 * Get all registered param names
 */
export function getRegisteredParamNames(): string[] {
  return Array.from(paramateInputRegistry.keys());
}

/**
 * Apply min/max constraints from param data to registered inputs.
 * Writes to the 'auto-min'/'auto-max' attributes (param-owned) on elements whose class
 * observes them. Author-set 'min'/'max' take precedence in the element, so these are
 * written unconditionally and refresh on every configure.
 */
export function applyParamConstraints(paramName: string, paramData: { min?: number; max?: number }): void {
  const entries = paramateInputRegistry.get(paramName);
  if (!entries) return;

  for (const entry of entries) {
    const el = entry.element.deref();
    if (!el) continue;

    const observed = (el.constructor as any).observedAttributes as string[] | undefined;
    if (!observed) continue;

    // Param-driven bounds are written to the `auto-*` attributes (no guard) so they keep
    // updating on every configure. The element combines them with any author-set
    // `min`/`max` by taking the most restrictive of the two.
    if (paramData.min !== undefined && observed.includes('auto-min')) {
      el.setAttribute('auto-min', String(paramData.min));
    }
    if (paramData.max !== undefined && observed.includes('auto-max')) {
      el.setAttribute('auto-max', String(paramData.max));
    }
  }
}

addAsGlobals({ paramateInputRegistry });
